import { useCallback } from "react";
import { useSession } from "@clerk/clerk-react";
import { useSupabaseStore } from "@/store/zustand/supabaseStore";
import { useAiActionStore, AI_MODEL } from "@/store/zustand/aiActionStore";
import { processStreamingChunk } from "@/utils/claudeCodeMessageTransformer";
import { toast } from "sonner";
import { track } from "@/utils/events/tracker";
import { v4 as uuidv4 } from "uuid";
import {
  parseMessageContentBlocks,
  useCreateMessageForChat,
  toClaudeCodeUserMessage,
} from "@/components/AI/chat/utils/messages";
import { countEligibleTokensFromSegments } from "@/utils/tokenCounter";
import { generateSSEStream } from "@/utils/ai/utils";
import { UploadedFile } from "@/utils/types";
import { useIframePoolStore } from "@/components/Canvas/IframePool/iframePoolStore";
import { DevErrorJson } from "@/store/zustand/devServerStore";
import { getAiMessageFromErrors } from "@/components/Canvas/hooks/useStoryboardErrors";

export interface SendClaudeCodeMessageOptions {
  message: string;
  conversationId?: string;
  attachedFiles?: UploadedFile[];
  includeErrorsIfAny?: boolean;
  allErrors?: DevErrorJson[];
  refreshAfterResponse?: boolean;
  additionalInstructions?: string;
}

// Map AI_MODEL to Claude Code model strings
// https://support.claude.com/en/articles/11940350-claude-code-model-configuration
const CLAUDE_CODE_MODEL_MAP: Partial<Record<AI_MODEL, string>> = {
  [AI_MODEL.CLAUDE_4_5_HAIKU]: "claude-haiku-4-5-20251001",
  [AI_MODEL.CLAUDE_4_6_SONNET]: "claude-sonnet-4-6",
  [AI_MODEL.CLAUDE_4_6_OPUS]: "claude-opus-4-6",
};

export const useSendClaudeCodeMessage = (
  canvasId: string,
  loadClaudeCodeMessages: (
    sessionId: string,
    conversationId: string,
  ) => Promise<void>,
) => {
  const { session } = useSession();
  const { createUserMessage } = useCreateMessageForChat();

  // Send message to Claude Code API using Server-Sent Events
  const sendClaudeCodeMessage = useCallback(
    async ({
      message,
      conversationId,
      attachedFiles,
      includeErrorsIfAny,
      allErrors,
      refreshAfterResponse,
      additionalInstructions,
    }: SendClaudeCodeMessageOptions): Promise<void> => {
      if (!session || !canvasId) {
        throw new Error("No session or canvas ID available");
      }

      try {
        // Get authentication token
        const token = await session.getToken({ template: "tempo-claude-code" });

        if (!token) {
          throw new Error("Failed to get Claude Code authentication token");
        }

        // Get supabase client
        const supabaseClient = useSupabaseStore.getState().supabaseClient;
        if (!supabaseClient) {
          throw new Error("Supabase client not available");
        }

        const claudeCodeApiUrl = `${
          import.meta.env.VITE_BACKEND_BASE_URL
        }/canvases/${canvasId}/claude-code`;

        // Get existing messages for the conversation
        const existingMessages = conversationId
          ? Object.values(useSupabaseStore.getState().aiChatMessages)
              .filter((msg) => msg.conversation_id === conversationId)
              .sort(
                (a, b) =>
                  new Date(a.created_at).getTime() -
                  new Date(b.created_at).getTime(),
              )
          : [];

        let finalMessage = message;

        if (includeErrorsIfAny && allErrors?.length) {
          const errorsMessage = getAiMessageFromErrors(allErrors);
          finalMessage = `${errorsMessage.content}\n\n${finalMessage}`;
        }

        if (additionalInstructions) {
          finalMessage = `${finalMessage}\n\n${additionalInstructions}`;
        }

        const userMessage = createUserMessage({
          userPrompt: finalMessage,
          attachedFiles,
          chatOnlyMode: false, // Claude Code supports tools
        });

        // Set up streaming messages for immediate UI update
        useAiActionStore
          .getState()
          .setChatStreamingMessages([...existingMessages, userMessage]);

        // Include session ID in options.resume for existing conversations to maintain context
        const conversation = conversationId
          ? useSupabaseStore.getState().aiChatConversations[conversationId]
          : undefined;
        const sessionId = conversation?.ai_provider_session_id;

        // Only use resume if we have a real session ID (not the placeholder)
        const shouldResume = sessionId && sessionId !== "claude_code";
        const options: Record<string, any> = shouldResume
          ? { resume: sessionId }
          : {};

        // Get selected model from store and include in options
        const codeGenerationModel =
          useAiActionStore.getState().codeGenerationModel;
        const claudeCodeModel = CLAUDE_CODE_MODEL_MAP[codeGenerationModel];
        if (claudeCodeModel) {
          options.model = claudeCodeModel;
        }

        // Generate unique trace ID for this request
        const traceId = uuidv4();

        // Reset token count at the start (same as regular AI)
        useAiActionStore.getState().resetStreamingTokenCount();

        // Convert userMessage to Claude Code compliant format
        const claudeCodeUserMessage = toClaudeCodeUserMessage(userMessage);

        // Track Claude Code message request
        track("AI_PROMPT_REQUESTED", {
          type: "AI_CLAUDE_CODE_CHAT",
          prompt: message,
          conversation_id: conversationId,
          traceId,
          claude_code_session_id: sessionId,
        });

        // Track streaming start
        const streamingStart = performance.now();
        track("AI_CHAT_PERFORMANCE", {
          task: "useSendClaudeCodeMessage",
          event: "streaming_start",
          traceId,
          conversationId,
          claude_code_session_id: sessionId || "",
        });

        try {
          // Use generateSSEStream with POST for Claude Code (handles Server-Sent Events format)
          // Include authHeader as query param for claude-code-proxy middleware
          const stream = await generateSSEStream(
            `${claudeCodeApiUrl}/chat?authHeader=${encodeURIComponent(
              `Bearer ${token}`,
            )}`,
            {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
              },
              body: JSON.stringify({
                message: claudeCodeUserMessage,
                options,
              }),
            },
          );

          // Keep track of current streaming messages
          let currentStreamingMessages = [...existingMessages, userMessage];
          let streamCompleted = false;

          // Process the stream
          for await (const chunk of stream) {
            try {
              const data = chunk; // Already parsed by generateSSEStream

              // Check if Claude Code returned a session ID (for new conversations or resumed ones)
              if (data.session_id && conversationId && supabaseClient) {
                const store = useSupabaseStore.getState();
                const existingConversation =
                  store.aiChatConversations[conversationId];

                // Update if this is a new session (placeholder -> real) or different session (resume created new)
                if (
                  existingConversation &&
                  (existingConversation.ai_provider_session_id ===
                    "claude_code" ||
                    existingConversation.ai_provider_session_id !==
                      data.session_id)
                ) {
                  const updatedConversation = {
                    ...existingConversation,
                    ai_provider_session_id: data.session_id,
                  };

                  // Update store first (immediate UI update)
                  store.onDbSnapshot("aiChatConversations", {
                    newItems: [updatedConversation],
                    deletedItems: [],
                  });

                  // Update database (persistence)
                  try {
                    const { error: updateError } = await supabaseClient
                      .from("ai_chat_conversations")
                      .update({ ai_provider_session_id: data.session_id })
                      .eq("id", conversationId);

                    if (updateError) {
                      console.error(
                        "Failed to update session ID in database:",
                        updateError,
                      );
                    }
                  } catch (error) {
                    console.error(
                      "Error updating session ID in database:",
                      error,
                    );
                  }
                }
              }

              const updatedMessages = processStreamingChunk(
                data,
                currentStreamingMessages,
                conversationId || "",
                canvasId,
              );

              // Update if messages changed
              if (updatedMessages !== currentStreamingMessages) {
                currentStreamingMessages = updatedMessages;
                // Update streaming messages for real-time UI
                useAiActionStore
                  .getState()
                  .setChatStreamingMessages(updatedMessages);

                // Update streaming token count (same logic as regular AI)
                const latestMessage =
                  updatedMessages[updatedMessages.length - 1];
                if (latestMessage && latestMessage.role === "assistant") {
                  const segments = parseMessageContentBlocks(latestMessage);
                  const tokenCount = countEligibleTokensFromSegments(segments);
                  useAiActionStore
                    .getState()
                    .setStreamingTokenCount(tokenCount);
                }
              }
            } catch (error) {
              console.error("Error processing Claude Code message:", error);
            }

            // Check if streaming is complete or errored
            if (chunk.type === "result") {
              streamCompleted = true;
              break;
            }

            // Check if streaming encountered an error
            if (chunk.type === "error") {
              streamCompleted = true; // Treat error as completion to clean up state
              break;
            }
          }

          // After stream completes
          if (streamCompleted) {
            // Track streaming completion
            track("AI_CHAT_PERFORMANCE", {
              task: "useSendClaudeCodeMessage",
              event: "streaming_complete",
              durationMs: Math.round(performance.now() - streamingStart),
              traceId,
              conversationId,
              claude_code_session_id: sessionId || "",
            });

            // Clear streaming state
            useAiActionStore.getState().setChatStreamingMessages(null);
            // Reset token count when streaming completes (same as regular AI)
            useAiActionStore.getState().resetStreamingTokenCount();

            // Refresh storyboards if requested (e.g., for Fix with AI)
            if (refreshAfterResponse) {
              // Provide a small amount of time for the changes to compile
              setTimeout(() => {
                useIframePoolStore.getState().refreshAllStoryboards();
              }, 200);
            }

            // Always reload messages after streaming completion to get final state from Claude Code
            if (conversationId) {
              // Get the current session ID (may have been updated during streaming)
              const updatedConversation =
                useSupabaseStore.getState().aiChatConversations[conversationId];
              const currentSessionId =
                updatedConversation?.ai_provider_session_id;

              if (currentSessionId && currentSessionId !== "claude_code") {
                // Clear existing messages for this conversation to force a refresh
                const existingMessages = Object.values(
                  useSupabaseStore.getState().aiChatMessages,
                ).filter((msg) => msg.conversation_id === conversationId);

                if (existingMessages.length > 0) {
                  useSupabaseStore.getState().onDbSnapshot("aiChatMessages", {
                    newItems: [],
                    deletedItems: existingMessages,
                  });
                }

                await loadClaudeCodeMessages(currentSessionId, conversationId);

                // Track successful completion
                track("AI_CHAT_PERFORMANCE", {
                  task: "useSendClaudeCodeMessage",
                  event: "message_reload_complete",
                  durationMs: Math.round(performance.now() - streamingStart),
                  traceId,
                  conversationId,
                  claude_code_session_id: currentSessionId,
                });
              }
            }
          }
        } catch (error: any) {
          console.error("Claude Code streaming error:", error);

          // Track error
          track("AI_CHAT_ERROR", {
            error: "Claude Code streaming failed",
            data: {
              claude_code: true,
              error_message: error.message,
              conversationId,
              claude_code_session_id: sessionId,
            },
            traceId,
          });

          // Show error toast to user
          toast.error(
            `Claude Code Error: ${error.message || "Failed to stream response"}`,
          );

          // Clear streaming state on error
          useAiActionStore.getState().setChatStreamingMessages(null);
          useAiActionStore.getState().resetStreamingTokenCount();

          throw error;
        }
      } catch (err: any) {
        console.error("Failed to send Claude Code message:", err);
        throw err;
      }
    },
    [session, canvasId, loadClaudeCodeMessages, createUserMessage],
  );

  return {
    sendClaudeCodeMessage,
  };
};

export default useSendClaudeCodeMessage;
